import { isNonEmptyString, omit } from 'ts-utils';
import { ApiClient } from '../api/ApiClient';
import {
  isCorrectFormatForReferralId,
  MEDICARE_ACCESS_ENTRY,
  UrlKey,
} from '../config/AssessmentUtils';
import { EntryState, Referral } from '../store/entry/EntryState';
import { recordError } from '../util/Errors';
import { GeoLocate } from './GeoLocate';

/** How long the first screen waits for geolocation before falling back to AU. */
export const GEOLOCATION_TIMEOUT_SECONDS = 3;

export type EntryClients = Pick<ApiClient, 'getClientCountry' | 'getBasicReferralInfo'>;

/**
 * Resolves the user's Entry from the URL they arrived on (`search`), geolocation and the referral
 * lookup. Never throws: geolocation falls back to AU, and a referral id that is malformed, unknown
 * or fails to load leaves the user Direct. Each failure is recorded.
 */
export async function resolveEntry(
  search: string,
  clients: EntryClients,
  geolocationTimeoutSeconds = GEOLOCATION_TIMEOUT_SECONDS
): Promise<EntryState> {
  const params = new URLSearchParams(search);
  const [location, referral] = await Promise.all([
    GeoLocate.locate(clients, geolocationTimeoutSeconds),
    lookUpReferral(clients, params.get(UrlKey.REFERRAL_ID) ?? undefined),
  ]);
  const aslPaymentLinkId = params.get(UrlKey.ASL);
  return {
    ...location,
    referral,
    isReengagementLink: params.get(UrlKey.ENTRY) === MEDICARE_ACCESS_ENTRY,
    aslPaymentLinkId: isNonEmptyString(aslPaymentLinkId) ? aslPaymentLinkId : undefined,
    couponCode: params.get(UrlKey.COUPON) ?? undefined,
  };
}

/** The referral, if the backend confirms it. */
async function lookUpReferral(
  clients: EntryClients,
  referralId: string | undefined
): Promise<Referral | undefined> {
  if (referralId === undefined) return undefined;
  if (!isCorrectFormatForReferralId(referralId)) {
    recordError(new Error(`Malformed referral id '${referralId}' left the user Direct`));
    return undefined;
  }
  try {
    const result = await clients.getBasicReferralInfo({ referralId });
    if (result.exists && result.referralId !== undefined) {
      return { ...omit(result, ['success', 'exists']), referralId: result.referralId };
    }
    recordError(new Error(`Unknown referral id '${referralId}' left the user Direct`));
  } catch (error) {
    recordError(error);
  }
  return undefined;
}
